import { eventStyles } from './constants';
import type { CalendarDay, Session, SessionGroup, SessionStatus } from './types';

const padNumber = (value: number) => value.toString().padStart(2, '0');

export const formatISODate = (date: Date) =>
  `${date.getFullYear()}-${padNumber(date.getMonth() + 1)}-${padNumber(
    date.getDate()
  )}`;

export const parseISODate = (value: string) => {
  const [year, month, day] = value.split('-').map(Number);
  return new Date(year, month - 1, day);
};

export const startOfDay = (date: Date) =>
  new Date(date.getFullYear(), date.getMonth(), date.getDate());

export const addDays = (date: Date, days: number) =>
  new Date(date.getFullYear(), date.getMonth(), date.getDate() + days);

export const startOfWeek = (date: Date) => {
  const start = startOfDay(date);
  return addDays(start, -start.getDay());
};

export const parseTimeToMinutes = (value: string) => {
  const [hours, minutes] = value.split(':').map(Number);
  return hours * 60 + minutes;
};

const formatTime = (value: string) => {
  const [hours, minutes] = value.split(':').map(Number);
  const period = hours >= 12 ? 'PM' : 'AM';
  const adjustedHours = ((hours + 11) % 12) + 1;
  return `${adjustedHours}:${padNumber(minutes)} ${period}`;
};

export const formatTimeRange = (start: string, end: string) =>
  `${formatTime(start)} - ${formatTime(end)}`;

export const formatDuration = (start: string, end: string) => {
  const duration = parseTimeToMinutes(end) - parseTimeToMinutes(start);
  return `${duration} mins`;
};

export const formatTimeFromMinutes = (value: number) => {
  const hours = Math.floor(value / 60);
  const minutes = value % 60;
  const period = hours >= 12 ? 'PM' : 'AM';
  const adjustedHours = ((hours + 11) % 12) + 1;
  return `${adjustedHours}:${padNumber(minutes)} ${period}`;
};

export const getInitials = (name: string) =>
  name
    .split(' ')
    .map((part) => part[0])
    .slice(0, 2)
    .join('');

export const isSameDay = (a: Date, b: Date) =>
  a.getFullYear() === b.getFullYear() &&
  a.getMonth() === b.getMonth() &&
  a.getDate() === b.getDate();

export const buildCalendarDays = (viewDate: Date): CalendarDay[] => {
  const year = viewDate.getFullYear();
  const month = viewDate.getMonth();
  const firstOfMonth = new Date(year, month, 1);
  const startDay = firstOfMonth.getDay();
  const daysInMonth = new Date(year, month + 1, 0).getDate();
  const daysInPrevMonth = new Date(year, month, 0).getDate();
  const days: CalendarDay[] = [];

  for (let i = 0; i < 42; i += 1) {
    const dayNumber = i - startDay + 1;
    if (dayNumber <= 0) {
      const date = new Date(year, month - 1, daysInPrevMonth + dayNumber);
      days.push({ date, inCurrentMonth: false });
      continue;
    }

    if (dayNumber > daysInMonth) {
      const date = new Date(year, month + 1, dayNumber - daysInMonth);
      days.push({ date, inCurrentMonth: false });
      continue;
    }

    days.push({ date: new Date(year, month, dayNumber), inCurrentMonth: true });
  }

  return days;
};

const getDateLabel = (date: Date, referenceDate: Date) => {
  const today = startOfDay(referenceDate);
  const tomorrow = addDays(today, 1);

  if (isSameDay(date, today)) {
    return 'Today';
  }

  if (isSameDay(date, tomorrow)) {
    return 'Tomorrow';
  }

  return date.toLocaleDateString('en-US', {
    month: 'long',
    day: 'numeric',
    year: 'numeric',
  });
};

export const groupSessionsByDate = (
  sessions: Session[],
  referenceDate: Date
) => {
  const sortedSessions = [...sessions].sort((a, b) => {
    if (a.date !== b.date) {
      return b.date.localeCompare(a.date); // Descending: latest dates first
    }
    return parseTimeToMinutes(b.start) - parseTimeToMinutes(a.start); // Descending: latest times first
  });

  return sortedSessions.reduce<SessionGroup[]>((groups, session) => {
    const lastGroup = groups[groups.length - 1];
    if (!lastGroup || lastGroup.date !== session.date) {
      const dateValue = parseISODate(session.date);
      groups.push({
        label: getDateLabel(dateValue, referenceDate),
        date: session.date,
        sessions: [session],
      });
      return groups;
    }

    lastGroup.sessions.push(session);
    return groups;
  }, []);
};

export const getEventStyle = (value: string) => {
  const hash = value.split('').reduce((acc, char) => acc + char.charCodeAt(0), 0);
  return eventStyles[hash % eventStyles.length];
};

export const mapBookingStatus = (status: string): SessionStatus => {
  const normalized = status.trim().toLowerCase();
  if (['active', 'in progress', 'ongoing'].includes(normalized)) {
    return 'Active';
  }
  if (['completed', 'complete', 'finished', 'done'].includes(normalized)) {
    return 'Completed';
  }
  if (['missed', 'no_show', 'student_no_show', 'coach_no_show', 'no-show'].includes(normalized)) {
    return 'Missed';
  }
  if (['cancelled', 'canceled', 'cancelled_by_coach', 'cancelled_by_student'].includes(normalized)) {
    return 'Cancelled';
  }
  return 'Scheduled';
};

export const getBookingStudentName = (booking: any) =>
  booking?.student_name ??
  booking?.studentName ??
  booking?.student?.name ??
  booking?.user?.name ??
  booking?.name ??
  'Student';

export const getBookingFocusArea = (booking: any) =>
  booking?.focus_area ??
  booking?.focusArea ??
  booking?.focus ??
  booking?.plan_name ??
  booking?.plan?.name ??
  'Coaching';
